import { LockState } from "components/asset-management-system/assetRiskUtils";
import { FilterOperator, FilterPathType } from "domain/asset-management/assetFilterConstants";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import {
    Asset,
    AssetDistributionRequest,
    AssetFilter,
    AssetSearchRequest,
} from "services/asset-management/AssetService";

export const LOCKED_DEVICES_PAGE_SIZE = 50;

export type LockPath = AssetPathKey.AUTOPILOT_ENROLLMENT_STATE | AssetPathKey.FIND_MY_IPHONE;

export const LOCK_PATHS: LockPath[] = [AssetPathKey.AUTOPILOT_ENROLLMENT_STATE, AssetPathKey.FIND_MY_IPHONE];

export const ALL_LOCK_TYPES = "ALL";

export type LockTypeFilter = typeof ALL_LOCK_TYPES | LockPath;

enum LockPathValue {
    ENROLLED = "ENROLLED",
    NOT_ENROLLED = "NOT_ENROLLED",
    LOCKED = "LOCKED",
    UNLOCKED = "UNLOCKED",
}

export interface LockDetail {
    lockPath: LockPath;
    lockState: LockState;
    rawValue: string;
}

export interface LockedDeviceAsset {
    id: string;
    lockState: LockState;
    lockDetails: LockDetail[];
}

const toRequestedLockPaths = (lockTypeFilter: LockTypeFilter): LockPath[] =>
    lockTypeFilter === ALL_LOCK_TYPES ? LOCK_PATHS : [lockTypeFilter];

const toExistsFilter = (lockPath: LockPath): AssetFilter => ({
    name: lockPath,
    value: "",
    operator: FilterOperator.EXIST,
    pathType: FilterPathType.KEYWORD,
});

export const buildLockedDeviceDistributionRequest = (
    lockTypeFilter: LockTypeFilter,
    pageNumber: number,
    viewRequest?: AssetSearchRequest
): AssetDistributionRequest => ({
    filters: toRequestedLockPaths(lockTypeFilter).map(toExistsFilter),
    viewRequest: {
        ...viewRequest,
        paths: [AssetPathKey.UUID, AssetPathKey.ASSET_ID, ...LOCK_PATHS],
        size: LOCKED_DEVICES_PAGE_SIZE,
        cursor: String(pageNumber - 1),
    },
});

const getPathValue = (asset: Asset, lockPath: LockPath): string | undefined => {
    const value = asset[lockPath];
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
};

const toLockState = (lockPathValue: string): LockState => {
    switch (lockPathValue.toUpperCase()) {
        case LockPathValue.ENROLLED:
        case LockPathValue.LOCKED:
            return LockState.LOCKED;
        case LockPathValue.NOT_ENROLLED:
        case LockPathValue.UNLOCKED:
            return LockState.UNLOCKED;
        default:
            return LockState.NOT_AVAILABLE;
    }
};

const toAggregatedLockState = (lockStates: LockState[]): LockState => {
    if (lockStates.includes(LockState.LOCKED)) {
        return LockState.LOCKED;
    }
    if (lockStates.includes(LockState.UNLOCKED)) {
        return LockState.UNLOCKED;
    }
    return LockState.NOT_AVAILABLE;
};

export const toLockedDeviceAssets = (assets: Asset[]): LockedDeviceAsset[] =>
    assets.map((asset) => {
        const locks = LOCK_PATHS.flatMap((lockPath) => {
            const value = getPathValue(asset, lockPath);
            return value === undefined ? [] : [{ lockPath, lockState: toLockState(value), rawValue: value }];
        });
        return {
            id: asset.assetId,
            lockState: toAggregatedLockState(locks.map((lock) => lock.lockState)),
            lockDetails: locks,
        };
    });
